import { ApiErrorResponse } from 'apisauce';
import { create } from 'zustand';
import { TeceoApiResponse } from '../../../lib/basicRepository';
import {
  HandleLoadingStatusProps,
  LoadingStatus,
  LoadingStatusStoreProps,
} from './loadingStatus.store.types';

export const useLoadingStatusStore = create<LoadingStatusStoreProps>((set, get) => {
  const startRequest = () => {
    set((state) => ({
      loadingStatus: LoadingStatus.LOADING,
      pendingRequests: state.pendingRequests + 1,
    }));
  };

  const settleRequest = (loadingStatus: LoadingStatus) => {
    const pendingRequests = Math.max(get().pendingRequests - 1, 0);
    set({ pendingRequests });

    if (loadingStatus === LoadingStatus.ERROR) {
      set({ loadingStatus: LoadingStatus.ERROR });
      return;
    }

    if (pendingRequests === 0 && get().loadingStatus === LoadingStatus.LOADING) {
      set({ loadingStatus: LoadingStatus.SAVED });
    }
  };

  return {
    loadingStatus: LoadingStatus.SAVED,
    pendingRequests: 0,
    setLoadingStatus: (loadingStatus) => set({ loadingStatus }),
    trackRequestStatus: async <T>(requestFn: () => Promise<TeceoApiResponse<T>>) => {
      startRequest();

      try {
        const response = await requestFn();
        settleRequest(response.ok ? LoadingStatus.SAVED : LoadingStatus.ERROR);

        return response;
      } catch (err: unknown) {
        settleRequest(LoadingStatus.ERROR);
        throw err;
      }
    },
    handleLoadingStatus: async <T, ErrorData = any>({
      requestFn,
      onSuccess,
      onFailure,
    }: HandleLoadingStatusProps<T, ErrorData>) => {
      startRequest();

      try {
        const response = await requestFn();

        if ((response as TeceoApiResponse<T>).ok) {
          settleRequest(LoadingStatus.SAVED);
          onSuccess?.();
          return response;
        }

        const errorData = (response as ApiErrorResponse<T>).data ?? {};
        throw new Error(typeof errorData === 'string' ? errorData : JSON.stringify(errorData));
      } catch (err: unknown) {
        settleRequest(LoadingStatus.ERROR);

        if (err instanceof Error && onFailure) {
          onFailure(JSON.parse(err.message));
        }

        throw err;
      }
    },
  };
});
